import { defineComponent, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  Button as VanButton,
  CellGroup,
  Field,
  NavBar as VanNavBar,
  showFailToast,
  showSuccessToast,
  showToast,
} from 'vant'
import { useAuthStore } from '@/stores/auth-store'
import { useCountdown } from '@/composables/use-countdown'
import { useServerCaptcha } from '@/composables/use-server-captcha'
import { renderPhoneSmsFields } from '@/components/sms-code-fields'
import {
  postApiV1CheckCode,
  postApiV1CheckRegisterInfo,
  postApiV1Login,
  postApiV1Register,
} from '@/api/sdk.gen'

function detectSource(): 'PC' | 'Android' | 'H5' | 'IOS' {
  // 套壳 APP 走 Capacitor，注入 window.Capacitor。WebView 的 UA "wv" 标识在套壳里被抹掉了，
  // 所以不能用 UA 区分 H5/APP，必须看 Capacitor 全局对象。
  const cap = (globalThis as unknown as { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } }).Capacitor
  if (cap?.isNativePlatform?.()) {
    const platform = cap.getPlatform?.()
    if (platform === 'ios') return 'IOS'
    if (platform === 'android') return 'Android'
  }
  const ua = navigator.userAgent
  if (/Mobile|Mobi|Android|iPhone|iPad|iPod/i.test(ua)) return 'H5'
  return 'PC'
}

export default defineComponent({
  name: 'RegisterPage',
  setup() {
    const router = useRouter()
  const goBack = useSmartBack()
    const authStore = useAuthStore()

    const phone = ref('')
    const captchaInput = ref('')
    const code = ref('')
    const password = ref('')
    const confirmPassword = ref('')
    const nickname = ref('')
    const sendLoading = ref(false)
    const submitLoading = ref(false)
    const {
      captchaId,
      captchaImage,
      refresh: refreshCaptcha,
      verify: verifyCaptcha,
    } = useServerCaptcha()
    const { countdown, isCounting, start } = useCountdown()

    async function handleSendCode() {
      if (phone.value.length !== 11) {
        showToast('请输入正确的手机号')
        return
      }
      if (!captchaInput.value) {
        showToast('请输入图形验证码')
        return
      }
      sendLoading.value = true
      try {
        // 先单独校验图形验证码（消费式，通过后 captcha_id 立即失效，下面必须刷新）
        if (!(await verifyCaptcha(captchaInput.value))) return

        const res = await postApiV1CheckRegisterInfo({
          body: {
            channel: 'phone',
            account: phone.value,
            nickname: nickname.value || undefined,
            password: password.value || undefined,
          } as never,
        })
        const body = (res.data ?? res.error) as { code: number; message: string } | undefined
        if (body?.code !== 0) {
          showFailToast(body?.message || '验证码发送失败')
          refreshCaptcha()
          return
        }
        showSuccessToast('验证码已发送')
        start()
      } catch {
        showFailToast('验证码发送失败')
        refreshCaptcha()
      } finally {
        sendLoading.value = false
      }
    }

    async function handleRegister() {
      if (phone.value.length !== 11) return showToast('请输入正确的手机号')
      if (!code.value) return showToast('请输入验证码')
      if (!password.value) return showToast('请输入密码')
      if (password.value.length < 6 || password.value.length > 16) {
        return showToast('密码长度为 6-16 位')
      }
      if (password.value !== confirmPassword.value) return showToast('两次密码输入不一致')
      submitLoading.value = true
      try {
        const checkRes = await postApiV1CheckCode({
          body: {
            channel: 'phone',
            account: phone.value,
            code: code.value,
            scene: 'register',
          },
        })
        const checkBody = (checkRes.data ?? checkRes.error) as
          | { code: number; message: string; data: { ticket: string } | null }
          | undefined
        if (checkBody?.code !== 0 || !checkBody.data?.ticket) {
          showFailToast(checkBody?.message || '验证码校验失败')
          return
        }

        const regRes = await postApiV1Register({
          body: {
            channel: 'phone',
            account: phone.value,
            ticket: checkBody.data.ticket,
            password: password.value,
            password_confirm: confirmPassword.value,
            nickname: nickname.value || undefined,
            source: detectSource(),
          } as never,
        })
        const regBody = (regRes.data ?? regRes.error) as
          | { code: number; message: string }
          | undefined
        if (regBody?.code !== 0) {
          showFailToast(regBody?.message || '注册失败')
          return
        }

        const loginRes = await postApiV1Login({
          body: { channel: 'phone', account: phone.value, password: password.value },
        })
        const loginBody = (loginRes.data ?? loginRes.error) as
          | {
              code: number
              message: string
              data: {
                token: string
                refresh_token: string
                nickname: string
                user_id?: number
              } | null
            }
          | undefined
        if (loginBody?.code !== 0 || !loginBody.data) {
          showSuccessToast('注册成功，请登录')
          router.replace('/login')
          return
        }
        const loginData = loginBody.data

        authStore.setAuth(
          loginData.token,
          {
            id: loginData.user_id,
            username: loginData.nickname || nickname.value,
            phone: phone.value,
          },
          loginData.refresh_token,
        )
        showSuccessToast('注册成功')
        router.replace('/my')
      } catch {
        showFailToast('注册失败，请稍后重试')
      } finally {
        submitLoading.value = false
      }
    }

    return () => (
      <div class="register-wrap">
        <VanNavBar fixed title="手机注册" leftArrow onClickLeft={() => goBack()} />

        <CellGroup class="register-body" border={false}>
          {renderPhoneSmsFields({
            phone,
            captchaInput,
            code,
            captchaImage: captchaImage.value,
            refreshCaptcha,
            codeButtonText: isCounting.value ? `${countdown.value}s` : '获取验证码',
            onGetCode: () => {
              if (!isCounting.value && !sendLoading.value) handleSendCode()
            },
          })}

          <Field
            v-model={password.value}
            label="密 码"
            placeholder="请输入6-16数字或字母密码"
            type="password"
            maxlength={32}
          />

          <Field
            v-model={confirmPassword.value}
            label="重复密码"
            placeholder="请再次输入密码"
            type="password"
            maxlength={32}
          />

          <Field
            v-model={nickname.value}
            label="用户昵称"
            placeholder="请设置您的昵称（选填）"
            maxlength={20}
          />
        </CellGroup>

        <VanButton class="primary-btn" loading={submitLoading.value} onClick={handleRegister}>
          完成
        </VanButton>

        {/* 引用 captchaId 防止 lint 报未使用（API body 复用同一 captcha 体系） */}
        <span class="hidden">{captchaId.value}</span>
      </div>
    )
  },
})
